import React, { useEffect, useState } from 'react';
import { Loader2, Eye, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import api, { formatApiError } from '@/lib/api';
import { formatDate } from '@/lib/utils';

const statusColors = {
  pending: 'bg-amber-100 text-amber-700',
  contacted: 'bg-blue-100 text-blue-700',
  confirmed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700'
};

const statusLabels = {
  pending: 'Pendiente',
  contacted: 'Contactado',
  confirmed: 'Confirmado',
  cancelled: 'Cancelado'
};

const eventTypes = {
  estancia: 'Estancia',
  daypass: 'Day Pass',
  boda: 'Boda',
  xv: 'XV Años',
  bautizo: 'Bautizo',
  corporativo: 'Evento Corporativo',
  cumpleanos: 'Cumpleaños',
  otro: 'Otro'
};

export default function AdminInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      const response = await api.get('/admin/event-inquiries');
      setInquiries(response.data);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateInquiry = async (id, status) => {
    setUpdating(true);
    try {
      await api.patch(`/admin/event-inquiries/${id}`, { status });
      fetchInquiries();
      setSelectedInquiry(null);
    } catch (error) {
      alert(formatApiError(error));
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#5C4033]" />
      </div>
    );
  }

  return (
    <div data-testid="admin-inquiries">
      <h1 className="font-serif text-3xl text-[#1A1A1A] mb-8">Consultas de Eventos</h1>

      {inquiries.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-12 text-center text-gray-500">
            No hay consultas de eventos
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {inquiries.map((inquiry) => (
            <Card key={inquiry.id} className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-medium text-[#1A1A1A]">{inquiry.name}</h3>
                      <Badge className={statusColors[inquiry.status]}>
                        {statusLabels[inquiry.status]}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500">
                      {eventTypes[inquiry.event_type]} • {inquiry.guests} invitados • {formatDate(inquiry.event_date)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {inquiry.email} • {inquiry.phone}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setSelectedInquiry(inquiry)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Ver Detalle
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Inquiry Detail Modal */}
      <Dialog open={!!selectedInquiry} onOpenChange={() => setSelectedInquiry(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Detalle de Consulta</DialogTitle>
          </DialogHeader>
          {selectedInquiry && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Nombre</p>
                  <p className="font-medium">{selectedInquiry.name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Email</p>
                  <p className="font-medium">{selectedInquiry.email}</p>
                </div>
                <div>
                  <p className="text-gray-500">Teléfono</p>
                  <p className="font-medium">{selectedInquiry.phone}</p>
                </div>
                <div>
                  <p className="text-gray-500">Tipo de Evento</p>
                  <p className="font-medium">{eventTypes[selectedInquiry.event_type]}</p>
                </div>
                <div>
                  <p className="text-gray-500">Fecha del Evento</p>
                  <p className="font-medium">{formatDate(selectedInquiry.event_date)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Invitados</p>
                  <p className="font-medium">{selectedInquiry.guests}</p>
                </div>
              </div>

              {selectedInquiry.message && (
                <div>
                  <p className="text-gray-500 text-sm">Mensaje</p>
                  <p className="text-sm bg-gray-50 p-3 rounded-lg mt-1">
                    {selectedInquiry.message}
                  </p>
                </div>
              )}

              <div className="border-t pt-4">
                <p className="text-sm text-gray-500 mb-2">Cambiar estado</p>
                <div className="flex flex-wrap gap-2">
                  {['pending', 'contacted', 'confirmed', 'cancelled'].map((status) => (
                    <Button
                      key={status}
                      variant={selectedInquiry.status === status ? 'default' : 'outline'}
                      size="sm"
                      disabled={updating || selectedInquiry.status === status}
                      onClick={() => updateInquiry(selectedInquiry.id, status)}
                      className={selectedInquiry.status === status ? 'bg-[#5C4033]' : ''}
                    >
                      {statusLabels[status]}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="border-t pt-4 flex gap-2">
                <a href={`mailto:${selectedInquiry.email}`} className="flex-1">
                  <Button variant="outline" className="w-full">
                    Enviar Email
                  </Button>
                </a>
                <a href={`tel:${selectedInquiry.phone}`} className="flex-1">
                  <Button className="w-full bg-[#5C4033] hover:bg-[#3D5A3E]">
                    Llamar
                  </Button>
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
